import { useEffect, useRef, useSyncExternalStore } from "react";
import { useTranslation } from "../i18n/useTranslation";
import { showStaleNotice, subscribeStaleBuild } from "../features/release/staleBuild";
import { scrollBehavior, scrollerOf } from "../lib/scrollerOf";

/**
 * A NEWER RELEASE IS LIVE: reload before sending anything (rc.9 item 2,
 * features/release/staleBuild.ts). Nothing on the page was sent; the reload
 * keeps only what the address carries, and the copy says details may need
 * entering again. Hidden while a send is in flight or uncertain: a reload
 * then would lose the attempt. Renders nothing otherwise.
 */

export function StaleBuildNotice() {
  const { t } = useTranslation();
  // The snapshot IS the answer (a boolean), so a change re-renders and the
  // React Compiler cannot cache a stale call.
  const shown = useSyncExternalStore(subscribeStaleBuild, showStaleNotice, showStaleNotice);
  const notice = useRef<HTMLDivElement | null>(null);
  // It appears where the step begins, while the person just pressed a button
  // at the bottom of a phone screen: bring it into view (never focus: it is
  // an alert, announced as it appears). By scrolling the element that
  // ACTUALLY scrolls to the top, where the notice is first: scrollIntoView
  // also moved an ancestor in Chromium and left the title under the step
  // header (screenshot review). Not a named area: at a short or zoomed height
  // (30em) and with large text the step's area stops scrolling and its page
  // does (v3.css), so the element is found by what it does (review of
  // ea16b8fc, P2-1). Instant when motion is reduced. ONE FRAME LATER: since
  // Review's action is in the page (Oct 24), the press has scrolled the step
  // down, and WebKit re-placed that scroll after the notice's own layout,
  // cancelling a scroll started at once (stale-build.spec, guest Review).
  useEffect(() => {
    const element = notice.current;
    if (!shown || !element) return;
    const behavior = scrollBehavior();
    const frame = requestAnimationFrame(() => {
      const scroller = scrollerOf(element);
      if (scroller) scroller.scrollTo?.({ top: 0, behavior });
      else element.scrollIntoView?.({ block: "nearest", behavior });
    });
    return () => cancelAnimationFrame(frame);
  }, [shown]);
  if (!shown) return null;
  // The action sits in the notice's own action row (`.b-notice-a`), as every
  // other notice's does: a full-width button beside the body column squeezed
  // the copy to one character per line (review of d8f834c, P1).
  return (
    <div ref={notice} className="b-notice t-warning b3-stale" role="alert" data-testid="stale-build">
      <div className="b-notice-b">
        <b>{t("release.stale.title")}</b>
        <p className="b-p">{t("release.stale.body")}</p>
        <div className="b-notice-a">
          <button type="button" className="b-btn v-primary s-sm" data-testid="stale-build-reload" onClick={() => window.location.reload()}>
            {t("release.stale.reload")}
          </button>
        </div>
        {/* For every cause (Web Director, 2026-10-15): Safari can serve a
            failed module from the tab's memory on reload, so only a new tab
            recovers (App's Error view measured it on an iPhone), and a reload
            that keeps showing this is the reader's cue. */}
        <p className="b-meta" data-testid="stale-build-fallback">{t("release.stale.fallback")}</p>
      </div>
    </div>
  );
}
